import { CdkScrollable, CdkVirtualScrollViewport, ScrollDispatcher } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { MatOption, MatOptionSelectionChange } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import deepcopy from 'deepcopy';
import { Observable, Observer, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { BaseGroupType, GroupViewBy } from 'src/AngularJS/Models/group-type.model';
import { AttributeCategoryModel, AttributeSelectorModel, AttributeType, SelectedCategoryModel } from 'src/shared/models/attribute-selector.models';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { ClearViewUtils } from 'src/shared/utils';
import { AttributeSelectorService } from './attribute-selector.service';
import { CVLocalizationService } from '../services/cv-localization.service';

@Component({
    selector: 'cv-attribute-selector',
    templateUrl: './attribute-selector.component.html',
    styleUrls: ['./attribute-selector.component.scss']
})
export class AttributeSelectorComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() pageName: string;
    @Input() attributeType: AttributeType;
    @Input() settingsManager?: SlideoutSettingsManager;
    @Input() showSelectedToggle: boolean;
    @Input() creatorId: number; //wallboards uses this instead of loginId for states
    @Output() categoryViewChanged = new EventEmitter<boolean>();
    @Output() selectedAttributesChanged = new EventEmitter<AttributeSelectorModel>();
    @Output() selectedLevelChanged = new EventEmitter<{ groupType: BaseGroupType, groupLevel: number, groupLevelName: string }>();
    @Output() noAttributes = new EventEmitter<boolean>();

    // mat-select components to interact with
    @ViewChildren(MatSelect) selects: QueryList<MatSelect>;
    @ViewChildren(CdkVirtualScrollViewport) cdkVirtualScrollViewPorts: QueryList<CdkVirtualScrollViewport>;
    @ViewChildren(MatOption) options: QueryList<MatOption>;

    emptyStateText: string;

    private _cancelledSubscription: Subscription = null;
    private _reloadSubscription: Subscription = null;
    private _beforeAppliedSubscription: Subscription = null;
    private _appliedSubscription: Subscription = null;
    private _scrollableSubscription: Subscription = null;
    public categories: AttributeCategoryModel[];
    private initialSelectedCategory: AttributeSelectorModel = null;
    selectedCategoryIds = new Set<number>();
    maxSelectableCategories: number;
    initialized = false;

    // debounce observers
    private _queueSaveObserver: Observer<void>;
    private _queueSaveObservable: Observable<void>;
    private _queueSavePromise: Promise<any>;

    lastAppliedModel: AttributeSelectorModel = null;
    loadedModel: AttributeSelectorModel = null;
    model: AttributeSelectorModel = {
        Type: null, // set in ngOnInit()
        SelectedCategories: []
    };

    categorySearchText = '';
    hasAll: { [categoryId: number]: boolean } = {};
    search = {};

    private _inCategoryView = false;
    get inCategoryView() {
        return this._inCategoryView;
    }
    set inCategoryView(val: boolean) {
        this._inCategoryView = val;
        this.categoryViewChanged.emit(val);
    }

    get baseGroupType() {
        return this.attributeType == AttributeType.Data ? BaseGroupType.DataAttribute : BaseGroupType.UserAttribute;
    }

    get selectableCategories() {
        if (!this.categories)
            return [];

        return this.categories?.filter(o => !this.selectedCategoryIds.has(o.CategoryID) && o.CategoryName.toLowerCase().includes(this.categorySearchText.toLowerCase()));
    }

    get categoriesById(): { [categoryId: number]: AttributeCategoryModel } {
        return this.categories.reduce((byId, cat) => {
            byId[cat.CategoryID] = cat;
            return byId;
        }, { });
    }

    constructor(
        public service: AttributeSelectorService,
        private changeDetector: ChangeDetectorRef,
        readonly scrollDispatcher: ScrollDispatcher,
        private localizationService: CVLocalizationService,
    ) {

    }

    async ngOnInit() {
        await this.loadLocalizations();
        this.handleEmptyTextByAttributeType();
    }

    async loadLocalizations() {
        const localizationPromises = [
            this.localizationService.GetSection("Admin_Attributes"),
            this.localizationService.GetSection("Controls"),
        ];
        await Promise.all(localizationPromises);
        this.emptyStateText = this.localizationService.getCachedText("Admin_Attributes", "No_Attributes_Created");
    }

    handleEmptyTextByAttributeType() {
        const attributeTypeText = this.attributeType === AttributeType.Data
            ? `<span class="font-weight-bold">${this.localizationService.getCachedText("Controls", "Data")}</span>`
            : `<span class="font-weight-bold">${this.localizationService.getCachedText("Controls", "User")}</span>`;

        // Replace placeholder {0} in the localized string with the correct attribute type text.
        this.emptyStateText = this.emptyStateText.replace("{0}", attributeTypeText);
    }


    ngAfterViewInit() {
        this.selects.changes.subscribe(() => {
            // replace "empty" flag of the select to use the actual model value rather than only the options that are visible
            let needsUpdate = false;
            this.selects.forEach(select => {
                const selectElement: ElementRef<HTMLElement> = select._elementRef;
                const categoryId = parseInt(selectElement.nativeElement.attributes.getNamedItem("category-id")?.value, 10);
                if (categoryId) {
                    Object.defineProperty(select, 'empty', { get: () => this.model.SelectedCategories.find(c => c.SelectedCategoryID == categoryId)?.SelectedAttributeIDs.length === 0, configurable: true });
                    needsUpdate = true;
                }
            });

            if (needsUpdate) {
                this.changeDetector.detectChanges();
            }
        });
    }

    setupScrollHandler() {
        // subscribe when one of our scrollable containers scroll
        this._scrollableSubscription = this.scrollDispatcher
            .scrolled()
            .pipe(filter<CdkScrollable>(scrollable => this.cdkVirtualScrollViewPorts.find(viewport => viewport === scrollable) != null))
            .subscribe((scrollable) => {
                // when scrolling we need to update the options if they are selected since they are being re-rendered by the CdkVirtualScroll.
                const element = scrollable.getElementRef();
                const categoryIdString = element.nativeElement.attributes.getNamedItem("category-id").value;
                let needsUpdate = false;

                for (const option of this.options) {
                    if (option._getHostElement().attributes.getNamedItem("category-id")?.value !== categoryIdString)
                        continue;

                    const categoryId = parseInt(categoryIdString, 10);
                    const selected = this.model.SelectedCategories.find(c => c.SelectedCategoryID == categoryId)?.SelectedAttributeIDs.includes(option.value);
                    if (selected && !option.selected) {
                        option.select();
                        needsUpdate = true;
                    } else if (!selected && option.selected) {
                        option.deselect();
                        needsUpdate = true;
                    }
                }

                if (needsUpdate) {
                    this.changeDetector.detectChanges();
                }
            });
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.settingsManager) {
            this.unsubscribeEvents();

            this.settingsManager?.onSelectedGroupViewByChanged.subscribe((viewBy: GroupViewBy) => {
                if (viewBy.GroupType != this.baseGroupType) {
                    this.model.GroupedCategoryID = null;
                    this.loadedModel.GroupedCategoryID = null; // this should have been saved by the other selector
                    if (this.model.SelectedCategories.length > 0)
                        this.queueSaveSelector()
                }
            });

            // subscribe to settings cancel to reset the model to the lastAppliedModel
            this._cancelledSubscription = this.settingsManager?.onSettingsCancelled.subscribe(async () => {
                if (this.lastAppliedModel != null)
                    this.loadFromModel(this.lastAppliedModel);
                else if (this.initialSelectedCategory != null) {
                    await this.loadFromModel(this.initialSelectedCategory);
                }
            });

            this._reloadSubscription = this.settingsManager?.onAttributeReloaded.subscribe((model: AttributeSelectorModel) => {
                if (model.Type == this.attributeType)
                    this.loadFromModel(model);
            });

            this._beforeAppliedSubscription = this.settingsManager?.beforeSettingsApplied.subscribe(() => {
                // need to wait for save before allowing apply
                this._queueSaveObserver?.complete();
                this._queueSaveObservable = null;
                this._queueSaveObserver = null;
                if (this._queueSavePromise) {
                    this.settingsManager?.waitBeforeApplying.push(this._queueSavePromise);
                }
            });

            // subscribe to settings applied to keep the lastAppliedModel up-to-date
            this._appliedSubscription = this.settingsManager?.onSettingsApplied.subscribe(() => {
                // loadedModel will match the value we last saved to the _temp state, which is now what is saved to the actual state
                this.lastAppliedModel = deepcopy(this.loadedModel);
                this.initialSelectedCategory = deepcopy(this.loadedModel);
            });
        }

        if (changes.attributeType) {
            this.model.Type = this.attributeType;
            const categoryInformation = await this.service.loadCategories(this.attributeType);
            this.maxSelectableCategories = categoryInformation.MaxSelectableCategories;
            this.categories = categoryInformation.AttributeCategoryModels;
            this.noAttributes.emit(this.categories.length == 0);
            
            let loadedModel = await this.service.loadSelectorModel(this.pageName, this.attributeType, this.creatorId);
            this.loadFromModel(loadedModel);
            this.lastAppliedModel = deepcopy(this.loadedModel);
            this.initialSelectedCategory = deepcopy(loadedModel);
            this.setupScrollHandler();
            this.initialized = true;
        }
    }

    ngOnDestroy() {
        this.unsubscribeEvents();
    }

    unsubscribeEvents() {
        this._cancelledSubscription?.unsubscribe();
        this._appliedSubscription?.unsubscribe();
        this._beforeAppliedSubscription?.unsubscribe();
        this._reloadSubscription?.unsubscribe();
        this._scrollableSubscription?.unsubscribe();
    }

    async loadFromModel(loadedModel: AttributeSelectorModel) {
        // initialize empty list of attributes until we load the values
        this.model.GroupedCategoryID = loadedModel.GroupedCategoryID;
        this.model.SelectedCategories = loadedModel.SelectedCategories.map(cat => ({
            SelectedCategoryID: cat.SelectedCategoryID,
            CategoryMask: cat.CategoryMask,
            CategorySet: cat.CategorySet,
            SelectedAttributeIDs: [],
        }));

        this.selectedCategoryIds = new Set(this.model.SelectedCategories.map(cat => cat.SelectedCategoryID));

        for (let i = 0; i < this.model.SelectedCategories.length; i++) {
            const category = loadedModel.SelectedCategories[i];
            const modelCategory = this.model.SelectedCategories[i];

            // load values needed for this category
            let values = await this.service.loadValues(category.SelectedCategoryID, this.attributeType);

            let selectedAttributes = category.SelectedAttributeIDs;
            this.hasAll[category.SelectedCategoryID] = selectedAttributes.includes(-1) || selectedAttributes.length == 0;
            if (this.hasAll[category.SelectedCategoryID]) {
                selectedAttributes = values.map(val => val.ID);
                selectedAttributes.push(-1, 0);
            }

            // update model with selected attributes
            modelCategory.SelectedAttributeIDs = selectedAttributes;
        }

        this.updateSettingsManagerModel();
        this.selectedAttributesChanged.emit(this.model);
        this.loadedModel = loadedModel;
    }

    getSelectorDisplay(category: SelectedCategoryModel) {
        if (this.hasAll[category.SelectedCategoryID])
            return this.service.localizationText.all;

        if (category.SelectedAttributeIDs.length > 1) {
            let attributeValues = this.service.attributeValues[this.attributeType][category.SelectedCategoryID];
            let namesToJoin = category.SelectedAttributeIDs
                .slice(0, 10) // max 10 iterations for performance
                .map(id => id == 0 ? this.service.localizationText.notSet : (attributeValues.find(v => v.ID == id)?.Name ?? ''));
            return ClearViewUtils.joinWithMax(namesToJoin, 15, ', ');
        }

        if (category.SelectedAttributeIDs.length < 1)
            return "";

        let id = category.SelectedAttributeIDs[0];

        if (id == 0)
            return this.service.localizationText.notSet;

        let value = this.service.attributeValues[this.attributeType][category.SelectedCategoryID].find(v => v.ID == id);
        if (value)
            return value.Name;
        else { // group no longer available, select All again
            this.hasAll[category.SelectedCategoryID] = false; // set to false for it to toggle
            this.onSelectAllToggled(category);
            return "";
        }
    }

    onGroupByCategoryClicked(categoryId: number) {
        this.model.GroupedCategoryID = categoryId;
        this.queueSaveSelector();

        this.settingsManager.showInitialLevelName = false

        // for module slideout to display
        this.selectedLevelChanged.emit({
            groupType: this.baseGroupType,
            groupLevel: categoryId,
            groupLevelName: this.categoriesById[categoryId].CategoryName,
        });

        // for other selectors if necessary
        this.settingsManager?.onSelectedGroupViewByChanged.next({
            GroupType: this.baseGroupType,
            GroupLevel: categoryId,
        });
    }

    onSelectAllToggled(category: SelectedCategoryModel) {
        if (this.service.attributeValues[this.attributeType][category.SelectedCategoryID] == null)
            return; // values have not loaded yet

        if (!this.hasAll[category.SelectedCategoryID]) {
            category.SelectedAttributeIDs = [-1, 0].concat(this.service.attributeValues[this.attributeType][category.SelectedCategoryID].map(val => val.ID));
        } else {
            category.SelectedAttributeIDs = [];
        }

        this.hasAll[category.SelectedCategoryID] = !this.hasAll[category.SelectedCategoryID];
    }

    onSelectedAttributeValueChanged(category: SelectedCategoryModel, change: MatOptionSelectionChange) {
        if (!change.isUserInput)
            return;

        if (this.service.attributeValues[this.attributeType][category.SelectedCategoryID] == null)
            return; // values have not loaded yet

        const valueId: number = change.source.value;
        const index = category.SelectedAttributeIDs.indexOf(valueId);

        if (index >= 0)
            category.SelectedAttributeIDs.splice(index, 1);
        else
            category.SelectedAttributeIDs.push(valueId);

        // unselect the "All" option when not all are selected
        const selectedValuesWithoutAll = category.SelectedAttributeIDs.filter(id => id !== -1);
        if (selectedValuesWithoutAll.length != this.service.attributeValues[this.attributeType][category.SelectedCategoryID].length + 1) { // +1 for "Not Set"
            category.SelectedAttributeIDs = selectedValuesWithoutAll;
            this.hasAll[category.SelectedCategoryID] = false;
        } else { // all options selected, make sure all is checked
            category.SelectedAttributeIDs = [-1].concat(selectedValuesWithoutAll);
            this.hasAll[category.SelectedCategoryID] = true;
        }
    }

    onSelectorOpen() {
        
    }

    onSelectorClose(category: SelectedCategoryModel) {            
        // If no attribute is selected for the currently selected category, select all attributes
        if (category.SelectedAttributeIDs?.length == 0)
            this.onSelectAllToggled(category);

        this.queueSaveSelector();
    }

    onAddAttributeClicked() {
        if (this.maxSelectableCategories <= this.selectedCategoryIds.size)
            return;

        this.inCategoryView = true;
    }

    onCategorySelected(category: AttributeCategoryModel) {
        let selectedCategory: SelectedCategoryModel = {
            SelectedCategoryID: category.CategoryID,
            SelectedAttributeIDs: [],
            CategoryMask: category.CategoryMask,
            CategorySet: category.CategorySet,
        };

        this.model.SelectedCategories.push(selectedCategory);
        this.selectedCategoryIds.add(category.CategoryID);
        this.inCategoryView = false;

        this.service.loadValues(category.CategoryID, this.attributeType).then(async (values) => {
            // select all on new category added
            selectedCategory.SelectedAttributeIDs = [-1, 0, ...values.map(val => val.ID)];
            this.queueSaveSelector();
            this.hasAll[selectedCategory.SelectedCategoryID] = true;
        });
    }

    onRemoveCategoryClicked(category: SelectedCategoryModel) {
        let categoryIndex = this.model.SelectedCategories.findIndex(cat => cat.SelectedCategoryID == category.SelectedCategoryID);
        if (categoryIndex >= 0) {
            this.model.SelectedCategories.splice(categoryIndex, 1);

            if (this.model.GroupedCategoryID == category.SelectedCategoryID) this.model.GroupedCategoryID = null

            this.queueSaveSelector();
        }

        this.selectedCategoryIds.delete(category.SelectedCategoryID);
    }

    onBackClicked() {
        this.inCategoryView = false;
    }

    queueSaveSelector() {
        if (this._queueSaveObserver == null) {
            this._queueSaveObservable = new Observable<void>(o => this._queueSaveObserver = o)
                .pipe(debounceTime(1000));

            this._queueSaveObservable.subscribe(async () => {
                this._queueSavePromise = this.saveSelectorModel();
            });
        }

        this._queueSaveObserver.next();
    }

    async saveSelectorModel() {
        let loadedJSON = JSON.stringify(this.loadedModel);
        let currentJSON = JSON.stringify(this.model);

        if (loadedJSON === currentJSON && this.model.GroupedCategoryID != null)
            return;

        // Load different Type model
        let diffModel = await this.service.loadSelectorModel(this.pageName, this.model.Type == 1 ? 0 : 1)

        switch (diffModel.Type) {
            case AttributeType.Data:
                this.settingsManager.dataAttributesModel = diffModel;
                break;
            case AttributeType.User:
                this.settingsManager.userAttributesModel = diffModel;
                break;
        }

        this.updateSettingsManagerModel();
        this.selectedAttributesChanged.emit(this.model);

        await this.service.saveSelectorModel(this.pageName, this.model, this.creatorId);
        this.loadedModel = JSON.parse(currentJSON);
    }

    updateSettingsManagerModel() {
        if (this.settingsManager != null) {
            switch (this.model.Type) {
                case AttributeType.Data:
                    this.settingsManager.dataAttributesModel = this.model;
                    break;
                case AttributeType.User:
                    this.settingsManager.userAttributesModel = this.model;
                    break;
            }
        }
    }
}
